import React from 'react';
import { IAuthState, IRootState } from '../../@types/redux';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router';
import DamHelper, { IDam } from '../../@types/model/dam/dam';
import DamFunctions from '../../store/dam/functions';
import CustomMapBox, { IMapMarker } from '../customComponents/map/CustomMapBox';
import { AppBar, Drawer, Icon, IconButton, LinearProgress, Paper, Toolbar, Typography } from '@material-ui/core';
import { Transitions } from '../customComponents/animations/Transitions';
import DamInfo from './Info';
import { createSelector } from 'reselect';
import { DAM_MAP_IMAGES } from '../../appConstants';

interface IDamMapProps extends RouteComponentProps<{
    Code : string;
}, Record<string, unknown>, Record<string, unknown>> {
    auth : IAuthState;
    dams : Array<IDam>;
    isLoadingDams : boolean;
}

interface IDamMapState {
    selectedDam ?: IDam;
}

class DamMap extends React.Component<IDamMapProps, IDamMapState> {
    private readonly mapZoom = 8;
    private readonly mapCenter : [number, number] = [30.1148622, -23.5520414];

    constructor(props : IDamMapProps) {
        super(props);
        this.state = {};
    }

    public readonly componentDidMount = () => {
        DamFunctions.getList(this.setSelectedDam);
    };

    private setSelectedDam : undefined | (() => void) = () => {
        const urlParams = new URLSearchParams(this.props.location.search);
        const damId = urlParams.get('Code') as string;
        this.onMarkerClick(damId);

        this.setSelectedDam = undefined;
    };

    private readonly selectMarker = (dam : IDam) => {
        this.setState({
            selectedDam: dam,
        });
    };
    
    private readonly onMarkerClick = (id : string) => {
        const dam = this.props.dams.slice().find(n => n.code === id);

        if (dam) {        
            this.selectMarker(dam);
        }
    };

    private onMoreInfo = () => {
        DamFunctions.setSelected(Object.assign({}, this.state.selectedDam));
    };

    private onCloseDrawer = () => {
        this.setState({
            selectedDam: undefined,
        });
    };

    public getData = (state : IDamMapState, props : IDamMapProps) => props.dams;

    public getFilteredData = createSelector(
        [this.getData],
        (dams) => {
            return dams.filter(n => n.geo);
        },
    );

    public readonly getMarkers = createSelector(
        [this.getFilteredData],
        (dams) => {
            return dams.map(n => ({
                coordinates: [n.geo?.longitude ?? 0, n.geo?.latitude ?? 0],
                iconSize: 0.35,
                icon: '1',
                id: n.code,
            } as IMapMarker));
        }
    );

    public render = () => {
        const { isLoadingDams } = this.props;
        const { selectedDam } = this.state;
        const dams = this.getFilteredData(this.state, this.props);
        const markers = this.getMarkers(this.state, this.props);

        return (<div className={'fdc flx1 p10 mh0 mw0'}>
            <div className={'flx1 fdc mh0 mw0'}>
                <Toolbar>
                    <Typography variant='h5' color='inherit'>
                        Dams
                    </Typography>
                    <span className={'flx1'} />
                    <div className={'fdr pt5 jcfe aic'}>
                        <div className={'fdr pt5'}>
                            <span style={{ paddingRight: '15px' }}><b>Total: </b>{dams.length}</span>
                        </div>
                    </div>
                </Toolbar>
                <div className='mnh4'>
                    { isLoadingDams && <LinearProgress />}
                </div>
                <Paper className={'flx1 fdr oxh'}>
                    {
                                            
                        dams.length > 0 &&
                        <CustomMapBox
                            id='dam_map'
                            enableAreas
                            geoLocationControl
                            navigationControl
                            showScale
                            layerControls
                            images={DAM_MAP_IMAGES}
                            onMarkerClick={this.onMarkerClick}
                            markers={markers}
                            selectedMarker={selectedDam?.code}
                        />
                    }
                </Paper>
            </div>
            <Drawer open={!!selectedDam} anchor='right' variant='persistent'>
                <div className={'w300 flx1 fdc'}>
                    <AppBar className='posr' position='static'>
                        <Toolbar variant='dense'>
                            <div className='h60' />
                            <IconButton color={'inherit'} onClick={this.onMoreInfo}><Icon color={'inherit'}>more_vert</Icon></IconButton>
                            <Typography color='inherit' variant='h6' className={'flx1 ml10'} noWrap={true}>
                                {!!selectedDam && selectedDam.code}
                            </Typography>
                            <IconButton color={'inherit'} onClick={this.onCloseDrawer}><Icon color={'inherit'}>close</Icon></IconButton>
                        </Toolbar>
                    </AppBar>
                    {
                        selectedDam &&
                        <div className='fdc flx1'>
                            <div className='fdr flx1'>
                                <div className={'fdc flx1'}>
                                    <Typography className={'fdr bcDarkBlue fw700 cw p10'} variant='h6'>
                                        SUMMARY
                                    </Typography>
                                    <div className={'fdr pl10 pr10 pt10 mb10'}>
                                        <Typography className={'flx1 fw600 bbw1 bocGrey pb10 pl10 pr10'} variant='subtitle1'>
                                            Code
                                        </Typography>
                                        <Typography className={'flx1 fw600 bbw1 bocGrey pb10 pl10 pr10'} variant='subtitle1'>
                                            Dam type
                                        </Typography>
                                    </div>
                                    <div className={'fdr mb10 pl10 pr10'}>
                                        <Typography className={'flx1 pl10 pr10 mnh48'} variant='subtitle1'>
                                            {selectedDam.code}
                                        </Typography>
                                        <Typography className={'flx1 pl10 pr10 mnh48'} variant='subtitle1'>
                                            {DamHelper.DamType[(selectedDam.damType)]}
                                        </Typography>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                </div>
                <DamInfo fullScreen transition={Transitions.Up} />
            </Drawer>
        </div>);
    };
}

const mapStateToProps = (state : IRootState) => {
    return {
        dams: state.dam.dams,
        dam: state.dam.selectedDam,
        isLoadingDams: state.dam.loading,
        auth: state.auth,
    };
};

export default connect(
    mapStateToProps,
)(withRouter(DamMap));
